import Task from '../services/Task';
import Todo from '../store/schema/Todo';
import { owaDateToDisplayDate } from 'owa-display-date';
import { OwaDate, getEwsRequestString } from 'owa-datetime';

/**
 * How to map each Todo property to a Task property
 */
interface PropertyDefinition {
    propertyName: keyof Task;
    getValue?: (this: void, value: any) => any;
}

/**
 * Map Todo properties to a PropertyDefinition.
 * Properties not in this map are considered not supported.
 */
const propertyMap: Partial<Record<keyof Todo, PropertyDefinition>> = {
    OrderDateTime: {
        propertyName: 'OrderDateTime',
        getValue: value => {
            return value ? value.toISOString() : null;
        },
    },
    IsReminderOn: {
        propertyName: 'IsReminderOn',
    },
    ReminderDateTime: {
        propertyName: 'ReminderDateTime',
        getValue: value => {
            return toDateTimeWithTimezone(value);
        },
    },
    DueDateTime: {
        propertyName: 'DueDateTime',
        getValue: value => {
            return toDateTimeWithTimezone(value);
        },
    },
    Subject: { propertyName: 'Subject' },
    Status: { propertyName: 'Status' },
    Importance: { propertyName: 'Importance' },
    Note: {
        propertyName: 'Body',
        getValue: value => {
            return toBodyType(value);
        },
    },
    LinkedEmail: {
        propertyName: 'LinkedEntity',
    },
};

/**
 * Takes the key/value of the property to be updated and returns a SetItemField.
 * @param todoPropertyName name of the property to be updated
 * @param value Value of the property to be updated
 */
export default function getUpdateForProperty(
    todoPropertyName: keyof Todo,
    value: any
): { key: keyof Task; value: string } {
    const mapping = propertyMap[todoPropertyName];

    if (!mapping) {
        return null;
    }

    const retValue = mapping.getValue ? mapping.getValue(value) : value;
    return { key: mapping.propertyName, value: retValue };
}

function toDateTimeWithTimezone(value: OwaDate): any {
    if (value === null) {
        return null;
    }

    // TZ_BUG(fpintos): check if this is another service which
    // cannot handle offsets in DateTime or if it is ok with it.
    return {
        DateTime: getEwsRequestString(owaDateToDisplayDate(value)),
        TimeZone: value.tz,
    };
}
function toBodyType(value: string): any {
    return {
        Content: value ? value : '',
        ContentType: 'Text',
    };
}
